import { ILayer } from '../layer.type';
import { _ } from '~shared/utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';
import { environment } from '~environments/environment';
import { SymbolLayout, SymbolPaint } from 'mapbox-gl';

const markerUrlBase = environment.assetRoot + '/data/T13/markers/';

const pumpLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-anchor': 'top',
  'text-radial-offset': 1,
  'text-size': 10,
};

const pumpPaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
};

const smallIconLayout: SymbolLayout = {
  'text-size': [
    'interpolate', ['linear'], ['zoom'],
    0, 3,
    13, 12,
    16, 15,
  ],
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    0, 0.2,
    10, 0.4,
    16, 1.0,
  ],
};

/**
 * Keski-Savon Vesi
 */
export interface KeskiSavonTenantConfig extends TenantConfig {

}

export default (): KeskiSavonTenantConfig => ({
  layerDefinitions: getLayers(),
  layerDefinitionDefaults: {
    metadata: {
      visibleByZoom: 6,
    },
  },
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 62.323,
    lng: 27.866,
  },
  defaultZoom: 11.40,
  alarmFilters: [{
    id: 2,
    name: _('Priority'),
    type: 'dropdown-multiple',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: 'A-kriittiset',
        value: 1,
        boolean: true
      },
      {
        name: 'B-kriittiset',
        value: 2,
        boolean: true
      },
      {
        name: 'C-kriittiset',
        value: 3,
        boolean: true
      }
    ]
  }],
  vivo: {
    diaryTaskType: '60d3020ee7f7c50485f598b2',
    lookupPropertyId: '61559d61da6bc6731fa1a7f2',
  }
});

// vectorURL and vectorSourceLayer are filled in from encrypted-data/T13.json by MapService.addLayer
export function getLayers(): ILayer[] {
  return [
    {
      id: 'JVP_jvpumppaamo',
      name: 'Jätevedenpumppaamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          url: markerUrlBase + 'Pumppu.png',
        },
        visible: true,
      },
      mapboxOptions: {
        layout: {
          ...pumpLayout,
          ...smallIconLayout,
          'text-field': ['get', 'Positiotunnus'],
        },
        paint: pumpPaint,
      },
      metadata: {
        uniqueBy: 'Positiotunnus',
        labelBy: 'Laitteen nimi',
        group: 'Pumppaamot',
        component: 'ElementDetailsComponent',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
          hideProperties: ['Rego sympoli']
        },
        measurementsFilters: {
          tunnus: 'Positiotunnus',
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'Positiotunnus',
          type: 'startsWith',
        }, {
          key: 'Nimi',
          type: 'startsWith',
        }]
      },
      useWorker: true,
    },
    //
    // LINE DATA STARTS HERE
    //
    {
      id: 'SVV',
      name: _('Rainwater network'),
      vectorSourceLayer: 'SVV',
      orderingWeight: 10,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#000080',
        strokeWeight: 1.5,
        visible: true,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'SVPAINE',
      name: _('High pressure rainwater network'),
      vectorSourceLayer: 'SVPAINE',
      orderingWeight: 10,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#000080',
        strokeWeight: 3.0,
        visible: true,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JVV',
      name: _('Wastewater network'),
      vectorSourceLayer: 'JVV',
      orderingWeight: 10,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#D2691E',
        strokeWeight: 1.5,
        visible: true,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'JVPAINE',
      name: _('High pressure wastewater network'),
      vectorSourceLayer: 'JVPAINE',
      orderingWeight: 10,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#D2691E',
        strokeWeight: 3.0,
        visible: true,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    {
      id: 'VJ',
      name: _('Runoff network'),
      vectorSourceLayer: 'VJ',
      orderingWeight: 10,
      selectable: true,
      style: {
        clickable: true,
        strokeColor: '#006400',
        strokeWeight: 1.5,
        visible: true,
        layerType: 'line',
        sourceType: 'vector',
      },
      metadata: {
        visibleByZoom: 10,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: { coordinates: true, properties: ['EntityHandle'] },
        uniqueBy: 'EntityHandle',
        labelBy: 'EntityHandle',
        group: 'Verkostot',
      },
      searchOptions: {
        interestingProperties: [{ key: 'EntityHandle', type: 'contains' }],
        searchable: false,
      },
      useWorker: true,
    },
    getSharedLayer('houseNumbers'),
    getSharedLayer('elevationContour'),
    getSharedLayer('depthContour'),
    getSharedLayer('groundWaterAreas'),
    getSharedLayer('drainageAreas'),
    getSharedLayer('basinOutlets'),
  ];
}
